import React, { useEffect, useMemo, useRef, useState } from 'react'
import CommonInput from '../CommonInput/CommonInput.tsx'
import CommonInputType from '../../helpers/types/CommonInput.interface.ts'
import { makeStyles } from '@material-ui/core/styles'
import menuStyle from '../Menu/style.ts'
import styles from './style.ts'
import SearchableField from './SearchableField.tsx'
import SearchableMenu from './SearchableMenu'
import Icon from '../Icon/Icon.tsx'

const useMenuStyles = makeStyles(menuStyle)
const useSearchStyles = makeStyles(styles)

type HtmlInputProps = Omit<React.HTMLProps<HTMLInputElement>, 'children' | 'onChange' | 'size'>

/**
 *
 */
export type SearchableInputOptionType = {
    value?: any
    text?: React.ReactNode
    title?: boolean
    selected?: boolean
    icon?: string
    disabled?: boolean
    renderComponent?: JSX.Element
}

interface PropsInterface extends HtmlInputProps, CommonInputType {
    options: SearchableInputOptionType[]
    value?: any
    onChange?(value: any): any
    onInputChange?(value: string): any
    disabled?: boolean
    fullWidth?: boolean
    placeholder?: string
    title?: string
    onOpen?: () => void
}

const normalizeOptions = (initialOptions: SearchableInputOptionType[]) => initialOptions.map((initialOption) => {
    const newOption: Record<string, any> = { ...initialOption }
    if (newOption.renderComponent) newOption.text = newOption.renderComponent
    return newOption
})

/**
 *
 */
const SearchableInput = ({
    size = 'large',
    rules,
    isValid,
    helperText,
    options: initialOptions = [{ disabled: true, text: 'No data', value: null }],
    value,
    onChange,
    onInputChange,
    disabled = false,
    fullWidth = false,
    placeholder,
    title = 'Select an option',
    onOpen,
    className,
    'data-testid': dataTestId = 'searchable-input-field',
    ...props
}: PropsInterface): JSX.Element => {
    const [isOpen, setIsOpen] = useState(false)
    const [inputValue, setInputValue] = useState('')
    const [searchValue, setSearchValue] = useState('')
    const [selectedValue, setSelectedValue] = useState(value || '')
    const [options, setOptions] = useState(normalizeOptions(initialOptions))
    const inputRef = useRef<HTMLInputElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)
    const menuClasses = useMenuStyles({ withIcon: false })
    const searchStyles = useSearchStyles()

    const filteredOptions = useMemo(() => {
        const q = searchValue.trim().toLowerCase()
        if (!q) return options
        return options.filter((opt) => `${opt.text ?? ''}`.toLowerCase().includes(q))
    }, [options, searchValue])

    const selectedLabel = options.find((opt) => opt.value === selectedValue)?.text || ''

    const openMenu = () => {
        if (disabled) return
        setIsOpen(true)
        if (onOpen) onOpen()
    }

    const closeMenu = () => setIsOpen(false)

    const resetInput = () => {
        setInputValue('')
        setSearchValue('')
        if (onInputChange) onInputChange('')
    }

    const clearTypedText = (event?: React.SyntheticEvent) => {
        event?.preventDefault()
        event?.stopPropagation()
        setInputValue('')
        setSearchValue('')
        if (onInputChange) onInputChange('')
    }

    const handleSelect = (item: SearchableInputOptionType) => {
        if (!item || item.disabled) return
        setSelectedValue(item.value)
        setInputValue(String(item.text ?? ''))
        setSearchValue('')
        setIsOpen(false)
        if (onChange) onChange(item.value)
    }

    const handleInputChange = (next: string) => {
        setInputValue(next)
        setSearchValue(next)
        if (onInputChange) onInputChange(next)
        if (!isOpen) openMenu()
    }

    const handleBlur = () => {
        window.setTimeout(() => {
            if (!menuRef.current?.contains(document.activeElement)) {
                closeMenu()
                if (!selectedValue) {
                    resetInput()
                } else if (!inputValue || inputValue !== String(selectedLabel ?? '')) {
                    setInputValue(String(selectedLabel ?? ''))
                    setSearchValue('')
                }
            }
        }, 0)
    }

    useEffect(() => {
        setOptions(normalizeOptions(initialOptions))
    }, [initialOptions])

    useEffect(() => {
        const item = options.find((opt) => opt.value === value)
        setSelectedValue(item?.value || '')
        if (item) {
            setInputValue(String(item.text ?? ''))
        }
    }, [value, options])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                closeMenu()
                if (!selectedValue) resetInput()
                else {
                    setInputValue(String(selectedLabel ?? ''))
                    setSearchValue('')
                }
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [selectedValue, selectedLabel])

    const renderClearIcon = () => {
        if (!(inputValue || selectedValue) || disabled) return null

        return (
            <span className={searchStyles.clearIcon} onMouseDown={clearTypedText} data-testid={`${dataTestId}-clear`}>
                <Icon name='close' color='neutral-700' size={20} />
            </span>
        )
    }

    return (
        <CommonInput
            value={selectedLabel || inputValue || null}
            rules={rules}
            isValid={isValid}
            helperText={helperText}
            size={size}
            fullWidth={fullWidth}
            className={className}
            label={props.label}
            data-testid={dataTestId}
            trailingIcon={isOpen ? 'menu-expanded' : 'menu-collapsed'}
            disabled={disabled}
        >
            <div className={`select ${isOpen ? 'input--focused' : ''}`} data-testid={dataTestId} style={styles.inputShell}>
                <SearchableField
                    inputRef={inputRef}
                    value={inputValue}
                    placeholder={placeholder || title}
                    disabled={disabled}
                    onOpen={openMenu}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    testId={`${dataTestId}-input`}
                    styles={styles}
                />
                {renderClearIcon()}

                {isOpen && !disabled && (
                    <div className={menuClasses.dropdownContainer} data-testid={`${dataTestId}-menu`} ref={menuRef} style={styles.menuContainer}>
                        <div className={`${menuClasses.dropdown} dropdown--full-width`} style={{ width: '100%', minWidth: '100%', maxHeight: 'none', overflowY: 'hidden' }}>
                            <div style={styles.dropdownList}>
                                <SearchableMenu
                                    options={filteredOptions}
                                    selectedValue={selectedValue}
                                    onSelect={handleSelect}
                                    menuClasses={menuClasses}
                                    testId={dataTestId}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </CommonInput>
    )
}

/**
 *
 */
export type SearchableInputType = PropsInterface

export default SearchableInput
